import React from 'react';
import { CheckerSelector, CheckerValue } from '../CheckerSelector';
import { Button } from '@scalingworks/react-admin-ui';
import { FaPlus, FaTrash, FaInfoCircle, FaUserCheck } from 'react-icons/fa';
import type { CheckerSchema } from '~/resources/loyalty/stamp-template/hooks/useStampingSchemas';
import { STAMP_CONFIG } from '~/resources/loyalty/stamp-template/config/stamp-config';

interface EligibilityCheckersManagerProps {
  checkers: CheckerValue[];
  onChange: (checkers: CheckerValue[]) => void;
  availableCheckers: CheckerSchema[];
  errors?: any;
  disabled?: boolean;
}

export const EligibilityCheckersManager: React.FC<EligibilityCheckersManagerProps> = ({
  checkers = [],
  onChange,
  availableCheckers,
  errors,
  disabled = false
}) => {
  // Helper function to create default checker
  const createDefaultChecker = (): CheckerValue => ({
    code: 'always-true',
    params: {}
  });

  // Add a new checker
  const handleAddChecker = () => {
    const newChecker: CheckerValue = {
      code: '',
      params: {}
    };
    onChange([...checkers, newChecker]);
  };

  // Remove a checker
  const handleRemoveChecker = (index: number) => {
    const updatedCheckers = checkers.filter((_, i) => i !== index);
    // Ensure at least one checker remains
    if (updatedCheckers.length === 0) {
      updatedCheckers.push(createDefaultChecker());
    }
    onChange(updatedCheckers);
  };

  // Update a specific checker
  const handleUpdateChecker = (index: number, checker: CheckerValue) => {
    const updatedCheckers = [...checkers];
    updatedCheckers[index] = checker;
    onChange(updatedCheckers);
  };

  // Initialize with at least one checker if empty
  React.useEffect(() => {
    if (checkers.length === 0) {
      onChange([createDefaultChecker()]);
    }
  }, []);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaUserCheck className="text-indigo-600 text-lg" />
          <h3 className="text-sm font-semibold text-gray-700">Eligibility Conditions</h3>
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-1">
          <FaInfoCircle />
          <span>All conditions must pass (AND logic)</span>
        </div>
      </div>

      {/* Checkers List */}
      <div className="space-y-3">
        {checkers.map((checker, index) => (
          <div key={index} className="relative">
            {/* Remove button */}
            {checkers.length > 1 && !disabled && (
              <button
                onClick={() => handleRemoveChecker(index)}
                className="absolute -top-2 -right-2 z-10 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors shadow-md"
                title="Remove condition"
              >
                <FaTrash className="text-xs" />
              </button>
            )}
            
            {/* Checker Selector */}
            <div className={index > 0 ? 'mt-2' : ''}>
              {index > 0 && (
                <div className="flex items-center justify-center mb-2">
                  <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    AND
                  </span>
                </div>
              )}
              <CheckerSelector
                value={checker}
                onChange={(updatedChecker) => handleUpdateChecker(index, updatedChecker)}
                availableCheckers={availableCheckers}
                error={errors?.[index]}
                label={`Condition ${index + 1}`}
                required={index === 0}
                disabled={disabled}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Add Button */}
      {!disabled && checkers.length < STAMP_CONFIG.rules.maxEligibilityCheckers && (
        <div className="flex justify-center pt-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleAddChecker}
            className="flex items-center gap-2"
          >
            <FaPlus className="text-xs" />
            Add Condition
          </Button>
        </div>
      )}
      
      {/* Limit reached message */}
      {!disabled && STAMP_CONFIG.rules.showLimitMessage && checkers.length >= STAMP_CONFIG.rules.maxEligibilityCheckers && (
        <div className="flex justify-center pt-2">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <FaInfoCircle />
            Maximum {STAMP_CONFIG.rules.maxEligibilityCheckers} eligibility conditions reached
          </p>
        </div>
      )}

      {/* Summary */}
      {checkers.length > 0 && checkers.every(c => c.code) && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm font-medium text-blue-800 mb-2">Summary:</p>
          <p className="text-xs text-blue-700">
            This template will be available when{' '}
            <strong>all</strong> of the above conditions are met.
            {checkers.some(c => c.code === 'always-true') && (
              <span className="block mt-1 text-blue-600">
                ℹ️ "Always True" condition makes this template universally available.
              </span>
            )}
          </p>
        </div>
      )}

      {/* Error Message */}
      {errors && typeof errors === 'string' && (
        <div className="p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-xs text-red-600 flex items-center gap-1">
            <span>⚠️</span>
            {errors}
          </p>
        </div>
      )}
    </div>
  );
};

export default EligibilityCheckersManager;